/**
 * Minimal pure PNG encoder — RGBA in, spec-valid PNG bytes out (D13).
 *
 * Why this exists: the C4 sampler (`sampleRgbaFrame`) returns RAW RGBA, and
 * the snapshot artifact must store as an image, not `.json` (the named D13
 * trap). The app has no image-encoding dependency, and adding a native one
 * for a 640×480 still would be a dependency decision (GATES rule 1.F) buying
 * capability ~80 lines of platform-pure code provide — testable in this
 * package's node suite, runnable identically under Hermes.
 *
 * The zlib stream uses STORED (uncompressed) deflate blocks: every PNG reader
 * accepts them, the encoder stays dependency-free and allocation-predictable,
 * and a 640×480 frame costs ~1.23 MB — small next to the video parts sharing
 * the retention class. Compression is a later optimization, never a contract
 * change: the object key and content type stay `image/png` either way.
 */

/** Stored-deflate blocks carry at most 65535 bytes each. */
const MAX_STORED_BLOCK = 0xffff;

const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a] as const;

/** CRC-32 (ISO 3309), the PNG chunk checksum. Table built once at module load. */
const CRC_TABLE: Uint32Array = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    table[n] = c >>> 0;
  }
  return table;
})();

function crc32(bytes: Uint8Array): number {
  let crc = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) {
    crc = (CRC_TABLE[(crc ^ bytes[i]!) & 0xff]! ^ (crc >>> 8)) >>> 0;
  }
  return (crc ^ 0xffffffff) >>> 0;
}

/** Adler-32, the zlib stream checksum (over the UNCOMPRESSED data). */
function adler32(bytes: Uint8Array): number {
  let s1 = 1;
  let s2 = 0;
  for (let i = 0; i < bytes.length; i++) {
    s1 = (s1 + bytes[i]!) % 65521;
    s2 = (s2 + s1) % 65521;
  }
  return ((s2 << 16) | s1) >>> 0;
}

function writeUint32BE(out: Uint8Array, offset: number, value: number): void {
  out[offset] = (value >>> 24) & 0xff;
  out[offset + 1] = (value >>> 16) & 0xff;
  out[offset + 2] = (value >>> 8) & 0xff;
  out[offset + 3] = value & 0xff;
}

/** One PNG chunk: length, 4-char type, data, CRC over type+data. */
function chunk(type: string, data: Uint8Array): Uint8Array {
  const out = new Uint8Array(12 + data.length);
  writeUint32BE(out, 0, data.length);
  for (let i = 0; i < 4; i++) out[4 + i] = type.charCodeAt(i);
  out.set(data, 8);
  writeUint32BE(out, 8 + data.length, crc32(out.subarray(4, 8 + data.length)));
  return out;
}

/**
 * Encode 8-bit RGBA pixels as a PNG.
 *
 * `rgba` is exactly `width × height × 4` bytes, row-major, as the sampler
 * hands them over — anything else is a caller bug and throws rather than
 * producing a silently-corrupt image.
 */
export function encodePng(width: number, height: number, rgba: Uint8Array): Uint8Array {
  if (!Number.isInteger(width) || !Number.isInteger(height) || width < 1 || height < 1) {
    throw new Error(`encodePng: invalid dimensions ${width}x${height}`);
  }
  if (rgba.length !== width * height * 4) {
    throw new Error(
      `encodePng: expected ${width * height * 4} RGBA bytes for ${width}x${height}, got ${rgba.length}`,
    );
  }

  // Raw scanline stream: every row prefixed with filter byte 0 (None).
  const rowBytes = width * 4;
  const raw = new Uint8Array(height * (1 + rowBytes));
  for (let y = 0; y < height; y++) {
    const rowStart = y * (1 + rowBytes);
    raw[rowStart] = 0;
    raw.set(rgba.subarray(y * rowBytes, (y + 1) * rowBytes), rowStart + 1);
  }

  // zlib wrapper with stored deflate blocks.
  const blockCount = Math.max(1, Math.ceil(raw.length / MAX_STORED_BLOCK));
  const zlib = new Uint8Array(2 + raw.length + blockCount * 5 + 4);
  zlib[0] = 0x78; // CMF: deflate, 32K window
  zlib[1] = 0x01; // FLG: no dict, fastest — (0x7801) % 31 === 0 as required
  let z = 2;
  for (let b = 0; b < blockCount; b++) {
    const start = b * MAX_STORED_BLOCK;
    const len = Math.min(MAX_STORED_BLOCK, raw.length - start);
    zlib[z++] = b === blockCount - 1 ? 1 : 0; // BFINAL on the last block only
    zlib[z++] = len & 0xff;
    zlib[z++] = (len >>> 8) & 0xff;
    zlib[z++] = ~len & 0xff;
    zlib[z++] = (~len >>> 8) & 0xff;
    zlib.set(raw.subarray(start, start + len), z);
    z += len;
  }
  writeUint32BE(zlib, z, adler32(raw));

  const ihdr = new Uint8Array(13);
  writeUint32BE(ihdr, 0, width);
  writeUint32BE(ihdr, 4, height);
  ihdr[8] = 8; // bit depth
  ihdr[9] = 6; // colour type: truecolour with alpha
  ihdr[10] = 0; // compression: deflate
  ihdr[11] = 0; // filter method 0
  ihdr[12] = 0; // no interlace

  const chunks = [chunk('IHDR', ihdr), chunk('IDAT', zlib), chunk('IEND', new Uint8Array(0))];
  const total = PNG_SIGNATURE.length + chunks.reduce((n, c) => n + c.length, 0);
  const png = new Uint8Array(total);
  png.set(PNG_SIGNATURE, 0);
  let offset = PNG_SIGNATURE.length;
  for (const c of chunks) {
    png.set(c, offset);
    offset += c.length;
  }
  return png;
}
